/**
 * React Router Compatibility Exports
 * 
 * This module re-exports React Router components and provides compatibility 
 * wrappers for deprecated APIs like withRouter.
 * 
 * Usage: Change imports from 'react-router-dom' to 'tools/routerCompat'
 * for components that need withRouter or other deprecated APIs.
 * 
 * Example:
 *   Before: import { withRouter, Link } from "react-router-dom";
 *   After:  import { withRouter, Link } from "tools/routerCompat";
 */
import React from "react";
import {
  Link,
  NavLink,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
  useMatch,
} from "react-router-dom";
import { useBlockerContext } from "containers/App/RouteCompat";

/**
 * withRouter - Compatibility HOC for React Router 7
 * 
 * In React Router v4/v5, withRouter provided match, location, and history props.
 * In React Router v7, these are accessed via hooks. This HOC bridges the gap.
 * 
 * @param {React.ComponentType} WrappedComponent - Component to wrap
 * @returns {React.ComponentType} - Component with router props injected
 */
export function withRouter(WrappedComponent) {
  function WithRouterWrapper(props) {
    const params = useParams();
    const location = useLocation();
    const rawNavigate = useNavigate();
    const blockerContext = useBlockerContext();
    
    // Use blocked navigate if available, otherwise raw navigate
    const navigate = blockerContext?.navigate || rawNavigate;
    
    // Create a history-like object for backwards compatibility
    const history = {
      push: (pathOrLocation, state) => {
        // Handle both history.push('/path', state) and history.push({ pathname, state })
        if (typeof pathOrLocation === 'object' && pathOrLocation !== null) {
          const { pathname, search, hash, state: locationState } = pathOrLocation;
          const path = pathname + (search || '') + (hash || '');
          navigate(path, { state: locationState });
        } else {
          navigate(pathOrLocation, { state });
        }
      },
      replace: (pathOrLocation, state) => {
        // Handle both history.replace('/path', state) and history.replace({ pathname, state })
        if (typeof pathOrLocation === 'object' && pathOrLocation !== null) {
          const { pathname, search, hash, state: locationState } = pathOrLocation;
          const path = pathname + (search || '') + (hash || '');
          navigate(path, { replace: true, state: locationState });
        } else {
          navigate(pathOrLocation, { replace: true, state });
        }
      },
      goBack: () => rawNavigate(-1),
      goForward: () => rawNavigate(1),
      go: (n) => rawNavigate(n),
      block: blockerContext?.block || (() => () => {}),
      location,
      length: window.history.length,
      action: 'PUSH', // Default, not fully accurate but provides compatibility
    };
    
    // Create match object for backwards compatibility
    const match = {
      params,
      path: location.pathname,
      url: location.pathname,
      isExact: true, // In v7, all matches are exact by default
    };
    
    return (
      <WrappedComponent 
        {...props} 
        match={match} 
        location={location} 
        history={history} 
        navigate={navigate}
        params={params}
      />
    );
  }
  
  // Copy static properties
  const wrappedName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
  WithRouterWrapper.displayName = `withRouter(${wrappedName})`;
  WithRouterWrapper.WrappedComponent = WrappedComponent;
  
  return WithRouterWrapper;
}

/**
 * RouteComponentProps - Type interface for components using withRouter
 * Provides TypeScript compatibility for the injected props
 */
// Note: For TypeScript files, you can use this interface:
// interface RouteComponentProps<Params = {}, State = unknown> {
//   match: { params: Params; path: string; url: string; isExact: boolean };
//   location: Location<State>;
//   history: History;
//   navigate: NavigateFunction;
// }

// Re-export all commonly used React Router exports
export {
  Link,
  NavLink,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
  useMatch,
};

// Re-export everything from react-router-dom for convenience
export * from "react-router-dom";
